import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { Edit2, Check, X } from 'lucide-react';
import { Userprofile } from '../Helpers/UsersContext';
import InternalUserSelector from './InternalUserSelector';

interface EditableUserFieldProps {
    value?: string; // selected internal-user UID (undefined = none)
    onSave: (userId: string | null) => Promise<void>;
    users: Userprofile[];
    getUserName: (uid: string) => string;
    label?: string;
    icon?: React.ReactNode;
    disabled?: boolean;
    /** Label for the empty option in edit mode */
    unassignedLabel?: string;
}

/**
 * EditableUserField
 * Inline-editable internal-user reference, styled and laid out exactly like
 * EditableDateField (grid cell: bold icon+label, value, pencil → dropdown +
 * save/cancel). Used for the informational beställare fields.
 */
export const EditableUserField: React.FC<EditableUserFieldProps> = ({
    value,
    onSave,
    users,
    getUserName,
    label,
    icon,
    disabled = false,
    unassignedLabel = 'Ingen vald',
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value ?? '');
    const [saving, setSaving] = useState(false);

    // Sync local state when the value prop changes
    useEffect(() => {
        setEditValue(value ?? '');
    }, [value]);

    const handleSave = async () => {
        if (editValue === (value ?? '')) {
            setIsEditing(false);
            return;
        }
        setSaving(true);
        try {
            await onSave(editValue || null);
            setIsEditing(false);
        } catch (err) {
            // Error handling is done in the parent component
            console.error('Error saving:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setEditValue(value ?? '');
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <div className="editable-date-field editing">
                {label && (
                    <div className="editable-date-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#000', fontSize: '14px', fontWeight: 700, marginBottom: '4px' }}>
                        {icon}{label}
                    </div>
                )}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <InternalUserSelector
                            users={users}
                            value={editValue}
                            onChange={setEditValue}
                            disabled={saving}
                            unassignedLabel={unassignedLabel}
                        />
                    </div>
                    <Button size="sm" variant="success" onClick={handleSave} disabled={saving}>
                        <Check size={16} />
                    </Button>
                    <Button size="sm" variant="secondary" onClick={handleCancel} disabled={saving}>
                        <X size={16} />
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="editable-date-field display">
            {label && (
                <div className="editable-date-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#000', fontSize: '14px', fontWeight: 700 }}>
                    {icon}{label}
                </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '1px', paddingLeft: icon ? '20px' : undefined }}>
                <span style={{ fontSize: '16px' }}>
                    {value ? getUserName(value) : <span style={{ color: '#999', fontStyle: 'italic' }}>Ej angiven</span>}
                </span>
                {!disabled && (
                    <Button
                        size="sm"
                        variant="link"
                        onClick={() => setIsEditing(true)}
                        style={{ padding: '2px 6px', color: '#666' }}
                    >
                        <Edit2 size={14} />
                    </Button>
                )}
            </div>
        </div>
    );
};
